import { validatePasswordComplexity, generateMessage, isUsingCompanyEmail } from '../utils';
import { OPTION_KEYS } from './optionKeys';
import { UI_OPTION_KEYS } from './uiOptionKeys';
import { LISTING_STATUSES, OWNERSHIP_BASES, OWNERSHIP_STRUCTURES } from '../ownershipCriteria';
import {
  SEARCH_CATEGORIES,
  PROFILER_SEARCH_CATEGORIES,
  PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES
} from './searchCategories';
// import { phone } from 'phone';
import { runInAction } from 'mobx';
import {
  FUNDS_VIEW_TABS,
  PORTFOLIOS_VIEW_TABS,
  INVEST_PROFILES_VIEW_TABS
} from '../../pages/Funds/constants';
import { GA_EVENTS, GA_DIMENSIONS } from '../../ga4/constants';
import {
  isProfilerAdvancedCriteriaDisabled,
  isProfilerAdvancedCriteriaSituationDisabled,
  isProfilerAdvancedCriteriaOwnershipDisabled
} from './variables';

const isOwnershipCriteriaHidden =
  isProfilerAdvancedCriteriaDisabled || isProfilerAdvancedCriteriaOwnershipDisabled;

export const FIELDS_TYPES = {
  TYPE_EMAIL: 'email',
  TYPE_PASSWORD: 'password',
  TYPE_TEXT: 'text',
  TYPE_NUMBER: 'number',
  TYPE_TEXTAREA: 'textarea',
  TYPE_SELECT: 'select',
  TYPE_MULTI_SELECT: 'multi_select',
  TYPE_RADIO: 'radio',
  TYPE_CHECKBOX: 'checkbox'
};

export const LOGIN_FIELDS = {
  EMAIL: {
    NAME: 'email',
    ID: 'email',
    LABEL: 'Email address',
    REQUIRED: true,
    HIDE_REQUIRED_LABEL_SYMBOL: true,
    PLACEHOLDER: 'Email',
    TYPE: FIELDS_TYPES.TYPE_EMAIL,
    FORMAT: (value = '') => value?.toString?.().toLowerCase?.() || ''
  },
  PASSWORD: {
    NAME: 'password',
    ID: 'password',
    LABEL: 'Password',
    REQUIRED: true,
    HIDE_REQUIRED_LABEL_SYMBOL: true,
    PLACEHOLDER: 'Password',
    TYPE: FIELDS_TYPES.TYPE_PASSWORD,
    VISIBILITY_TOGGLE: true,
    FORMAT: (value = '') => value.replace(/\s/g, '')
  }
};

// TD-1976 Phase 4 — single 6-digit code submitted to POST /mfa/verify.
// Kept as a field spec for consistency with the rest of fields.js even though
// the MFA setup page holds the value in local component state (useLocalObservable)
// rather than routing it through mapFieldsToState / <Input>. The OTP_REGEX is the
// canonical source for "is this string a valid TOTP code" used by both Phase 4
// verify and Phase 5 login MFA.
export const MFA_OTP_REGEX = /^\d{6}$/;

export const MFA_VERIFY_FIELDS = {
  USER_CODE: {
    NAME: 'userCode',
    ID: 'userCode',
    LABEL: 'Authentication code',
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_TEXT,
    VALIDATE: (value = '') => [
      generateMessage(
        'Enter the 6-digit code from your authenticator app',
        MFA_OTP_REGEX.test(value)
      )
    ]
  }
};

export const SIGNUP_FIELDS = {
  FIRST_NAME: {
    NAME: 'first_name',
    ID: 'first_name',
    LABEL: 'First name',
    REQUIRED: true,
    PLACEHOLDER: 'First name',
    TYPE: FIELDS_TYPES.TYPE_TEXT,
    SHOW_MESSAGES_ON_INPUT_VALUE: true,
    VALIDATE: (value = '') => {
      return [generateMessage('First name maximum text length is 25', value.length <= 25)];
    }
  },
  LAST_NAME: {
    NAME: 'last_name',
    ID: 'last_name',
    LABEL: 'Last name',
    REQUIRED: true,
    PLACEHOLDER: 'Last name',
    TYPE: FIELDS_TYPES.TYPE_TEXT,
    SHOW_MESSAGES_ON_INPUT_VALUE: true,
    VALIDATE: (value = '') => {
      return [generateMessage('Last name maximum text length is 25', value.length <= 25)];
    }
  },
  COMPANY_NAME: {
    NAME: 'company_name',
    ID: 'company_name',
    LABEL: 'Company',
    REQUIRED: true,
    PLACEHOLDER: 'Company',
    TYPE: FIELDS_TYPES.TYPE_TEXT,
    SHOW_MESSAGES_ON_INPUT_VALUE: true,
    VALIDATE: (value = '') => {
      return [generateMessage('Company name maximum text length is 50', value.length <= 50)];
    }
  },
  COMPANY_TYPE: {
    NAME: 'company_type',
    ID: 'company_type',
    LABEL: 'Company type',
    REQUIRED: true,
    PLACEHOLDER: 'Company type',
    TYPE: FIELDS_TYPES.TYPE_SELECT,
    VALUES_KEY: OPTION_KEYS.COMPANY_TYPES
  },
  EMAIL: {
    NAME: 'email',
    ID: 'email',
    LABEL: 'Email address',
    REQUIRED: true,
    PLACEHOLDER: 'Email address',
    TYPE: FIELDS_TYPES.TYPE_EMAIL,
    VALIDATE: (value = '') => {
      return [
        generateMessage('Please use your company email address', isUsingCompanyEmail(value)),
        generateMessage('Email maximum text length is 254', value.length <= 254)
      ];
    },
    FORMAT: (value = '') => value?.toString?.().toLowerCase?.() || ''
  },
  PHONE_CODE: {
    NAME: 'phone_code',
    ID: 'phone_code',
    LABEL: 'Country code',
    REQUIRED: false,
    PLACEHOLDER: 'Select',
    TYPE: FIELDS_TYPES.TYPE_SELECT,
    VALUES_KEY: OPTION_KEYS.PHONE_CODES
  },
  PHONE_NUMBER: {
    NAME: 'phone_number',
    ID: 'phone_number',
    LABEL: 'Phone number',
    REQUIRED: false,
    PLACEHOLDER: 'Phone',
    TYPE: FIELDS_TYPES.TYPE_TEXT,
    FORMAT: (value = '') => {
      const pattern = /\d+/g;
      const result = value.match(pattern);
      if (!result) {
        return '';
      }

      let formatted = result.join('');
      while (formatted.startsWith('0')) {
        formatted = formatted.slice(1);
      }

      return formatted;
    }
  },
  PASSWORD: {
    NAME: 'password',
    ID: 'password',
    LABEL: 'Password',
    REQUIRED: true,
    PLACEHOLDER: 'Password',
    TYPE: FIELDS_TYPES.TYPE_PASSWORD,
    VISIBILITY_TOGGLE: true,
    FORMAT: (value = '') => value.replace(/\s/g, ''),
    VALIDATE: validatePasswordComplexity
  },
  RE_PASSWORD: {
    NAME: 'repeatPassword',
    ID: 'repeatPassword',
    LABEL: 'Repeat password',
    REQUIRED: true,
    PLACEHOLDER: 'Repeat password',
    TYPE: FIELDS_TYPES.TYPE_PASSWORD,
    VISIBILITY_TOGGLE: true,
    FORMAT: (value = '') => value.replace(/\s/g, '')
  },
  TOS: {
    NAME: 'TOS',
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_CHECKBOX,
    INPUT_CLASSNAME: 'tos',
    LABEL_CLASSNAME: 'no-padding field-tos checkbox-font',
    ON_SUBMIT_ERROR_STATE_WRAP_CLASSNAME: 'checkbox-container',
    VALIDATE: (value) => {
      return [
        generateMessage(
          'Please agree with our Terms & conditions in order to sign up',
          value === true
        )
      ];
    }
  },
  // MARKETING_CONSENT: {
  //   NAME: 'marketing_consent',
  //   LABEL:
  //     'Yes, I agree that transact.digital GmbH may send me personalized advertisements and information tailored to my personal interests (e.g., newsletters, event invitations, product updates) via e-mail, SMS and instant messages from the corporate finance sector.',
  //   REQUIRED: false,
  //   TYPE: FIELDS_TYPES.TYPE_CHECKBOX,
  //   INPUT_CLASSNAME: 'tos',
  //   LABEL_CLASSNAME: 'no-padding field-tos checkbox-font'
  // },
  VERIF_CODE: {
    NAME: 'verificationCode',
    ID: 'code',
    LABEL: 'Verification code',
    REQUIRED: true,
    PLACEHOLDER: 'Verification code',
    TYPE: FIELDS_TYPES.TYPE_TEXT,
    INPUT_CLASSNAME: 'form-control verify-input',
    SHOW_MESSAGES_ON_INPUT_VALUE: true,
    FORMAT: (value = '') => {
      if (!new RegExp('^[0-9-]{0,11}$').test(value)) {
        return '';
      }

      return value.replace(/-/g, '').split('').join('-');
    },
    DEFORMAT: (value = '') => {
      return value.replaceAll('-', '');
    },
    VALIDATE: (value = '') => {
      const messages = [];

      let isValid = true;
      if (
        !new RegExp('^[0-9-]{0,11}$').test(value) ||
        value.length < SIGNUP_FIELDS.VERIF_CODE.MINLENGTH ||
        value.length > SIGNUP_FIELDS.VERIF_CODE.MAXLENGTH
      ) {
        isValid = false;
      }

      messages.push(generateMessage('Verification code must be 6 digits long', isValid));

      return messages;
    },
    MINLENGTH: 11,
    MAXLENGTH: 11
  }
};

export const FORGOTTEN_PWD_FIELDS = {
  EMAIL: {
    NAME: 'email',
    ID: 'email',
    LABEL: 'Email',
    REQUIRED: true,
    PLACEHOLDER: 'Email',
    TYPE: FIELDS_TYPES.TYPE_EMAIL,
    FORMAT: (value = '') => value?.toString?.().toLowerCase?.() || ''
  },
  CODE: {
    NAME: 'code',
    ID: 'code',
    LABEL: 'Code',
    REQUIRED: true,
    PLACEHOLDER: 'Code',
    TYPE: FIELDS_TYPES.TYPE_TEXT,
    SHOW_MESSAGES_ON_INPUT_VALUE: true,
    FORMAT: (value = '') => {
      if (!new RegExp('^[0-9-]{0,11}$').test(value)) {
        return '';
      }

      return value.replace(/-/g, '').split('').join('-');
    },
    DEFORMAT: (value = '') => {
      return value.replaceAll('-', '');
    },
    VALIDATE: (value = '') => {
      const messages = [];

      let isValid = true;
      if (
        !new RegExp('^[0-9-]{0,11}$').test(value) ||
        value.length < FORGOTTEN_PWD_FIELDS.CODE.MINLENGTH ||
        value.length > FORGOTTEN_PWD_FIELDS.CODE.MAXLENGTH
      ) {
        isValid = false;
      }

      messages.push(generateMessage('Code must be 6 digits long', isValid));

      return messages;
    },
    MINLENGTH: 11,
    MAXLENGTH: 11
  },
  PASSWORD: {
    NAME: 'password',
    ID: 'password',
    LABEL: 'New Password',
    REQUIRED: true,
    PLACEHOLDER: 'New Password',
    TYPE: FIELDS_TYPES.TYPE_PASSWORD,
    VISIBILITY_TOGGLE: true,
    VALIDATE: validatePasswordComplexity,
    SHOW_MESSAGES_ON_INPUT_VALUE: true
  },
  RE_PASSWORD: {
    NAME: 'passwordRepeat',
    ID: 'password_repeat',
    LABEL: 'Repeat password',
    REQUIRED: true,
    PLACEHOLDER: 'Repeat password',
    TYPE: FIELDS_TYPES.TYPE_PASSWORD,
    VISIBILITY_TOGGLE: true,
    SHOW_MESSAGES_ON_INPUT_VALUE: true
  }
};

export const USER_PROFILE_FIELDS = {
  FIRST_NAME: {
    NAME: 'firstName',
    ID: 'first_name',
    LABEL: 'First name',
    LABEL_DESCRIPTION: 'This will be visible on your profile',
    PLACEHOLDER: 'First name',
    TYPE: FIELDS_TYPES.TYPE_TEXT,
    REQUIRED: true,
    SHOW_MESSAGES_ON_INPUT_VALUE: true,
    VALIDATE: (value = '') => {
      return [generateMessage('First name maximum text length is 25', value.length <= 25)];
    }
  },
  LAST_NAME: {
    NAME: 'lastName',
    ID: 'last_name',
    LABEL: 'Last name',
    LABEL_DESCRIPTION: 'This will be visible on your profile',
    PLACEHOLDER: 'Last name',
    TYPE: FIELDS_TYPES.TYPE_TEXT,
    REQUIRED: true,
    SHOW_MESSAGES_ON_INPUT_VALUE: true,
    VALIDATE: (value = '') => {
      return [generateMessage('Last name maximum text length is 25', value.length <= 25)];
    }
  },
  EMPLOYER: {
    NAME: 'employer',
    ID: 'employer',
    LABEL: 'Employer',
    LABEL_DESCRIPTION: 'Who is your current employer',
    PLACEHOLDER: 'Employer',
    TYPE: FIELDS_TYPES.TYPE_TEXT,
    REQUIRED: true
  },
  JOB_POSITION: {
    NAME: 'jobPosition',
    ID: 'job_position',
    LABEL: 'Job position',
    LABEL_DESCRIPTION: 'What is your current position',
    PLACEHOLDER: 'Job position',
    TYPE: FIELDS_TYPES.TYPE_TEXT,
    SHOW_MESSAGES_ON_INPUT_VALUE: true,
    VALIDATE: (value = '') => {
      return [generateMessage('Job position maximum text length is 20', value.length <= 20)];
    }
  },
  EMAIL: {
    NAME: 'email',
    ID: 'email',
    LABEL: 'Email address',
    LABEL_DESCRIPTION: 'The email address you registered',
    PLACEHOLDER: 'Email address',
    TYPE: FIELDS_TYPES.TYPE_TEXT,
    REQUIRED: true
  },
  PHONE_NUMBER: {
    NAME: 'phoneNumber',
    ID: 'phone_number',
    LABEL: 'Phone number',
    LABEL_DESCRIPTION: 'Your preferred phone number',
    PLACEHOLDER: 'Phone number',
    TYPE: FIELDS_TYPES.TYPE_TEXT,
    FORMAT: (value = '') => {
      if (value === '+') {
        return '+';
      }

      const pattern = /\d+/g;
      const result = value.match(pattern);
      if (!result) {
        return '';
      }

      let formatted = result.join('');
      while (formatted.startsWith('0')) {
        formatted = formatted.slice(1);
      }

      return '+' + formatted;
    }
    // VALIDATE: (value = '') => {
    //   return [
    //     generateMessage(
    //       'Please enter valid phone number',
    //       phone(value, { strictDetection: true }).isValid
    //     )
    //   ];
    // }
  },
  CITY: {
    NAME: 'city',
    ID: 'city',
    LABEL: 'City',
    LABEL_DESCRIPTION: 'Your current location by city',
    PLACEHOLDER: 'City',
    TYPE: FIELDS_TYPES.TYPE_TEXT,
    SHOW_MESSAGES_ON_INPUT_VALUE: true,
    VALIDATE: (value = '') => {
      return [generateMessage('City maximum text length is 20', value.length <= 20)];
    }
  },
  COUNTRY: {
    NAME: 'country',
    ID: 'country',
    LABEL: 'Country',
    LABEL_DESCRIPTION: 'Your current location by country',
    PLACEHOLDER: 'Country',
    TYPE: FIELDS_TYPES.TYPE_SELECT,
    VALUES_KEY: OPTION_KEYS.PERSONAL_COUNTRIES
  }
};

export const USER_CHANGE_PASSWORD_FIELDS = {
  OLD_PASSWORD: {
    NAME: 'oldPassword',
    ID: 'old_password',
    LABEL: 'Current password',
    REQUIRED: true,
    PLACEHOLDER: 'Current password',
    TYPE: FIELDS_TYPES.TYPE_PASSWORD,
    VISIBILITY_TOGGLE: true
  },
  NEW_PASSWORD: {
    NAME: 'newPassword',
    ID: 'new_password',
    LABEL: 'New password',
    REQUIRED: true,
    PLACEHOLDER: 'New password',
    TYPE: FIELDS_TYPES.TYPE_PASSWORD,
    VISIBILITY_TOGGLE: true,
    VALIDATE: validatePasswordComplexity,
    SHOW_MESSAGES_ON_INPUT_VALUE: true
  },
  RE_NEW_PASSWORD: {
    NAME: 'repeatNewPassword',
    ID: 'repeat_new_password',
    LABEL: 'Repeat password',
    REQUIRED: true,
    PLACEHOLDER: 'Repeat new password',
    TYPE: FIELDS_TYPES.TYPE_PASSWORD,
    VISIBILITY_TOGGLE: true,
    SHOW_MESSAGES_ON_INPUT_VALUE: true
  }
};

export const USER_DEACTIVATE_ACCOUNT_FIELDS = {
  EMAIL: {
    NAME: 'email',
    ID: 'deactivate_email',
    LABEL: 'Email address',
    REQUIRED: true,
    LABEL_DESCRIPTION: 'Enter your account email address to start account deactivation',
    PLACEHOLDER: 'Email',
    TYPE: FIELDS_TYPES.TYPE_EMAIL,
    FORMAT: (value = '') => value?.toString?.().toLowerCase?.() || ''
  }
};

export const COMPANY_PROFILE_FIELDS = {
  COMPANY_NAME: {
    NAME: 'companyName',
    ID: 'company_name',
    LABEL: 'Official company name',
    LABEL_DESCRIPTION: 'Your legal company name',
    REQUIRED: true,
    PLACEHOLDER: 'Company name',
    TYPE: FIELDS_TYPES.TYPE_TEXT,
    SHOW_MESSAGES_ON_INPUT_VALUE: true,
    VALIDATE: (value = '') => {
      return [generateMessage('Company name maximum text length is 50', value.length <= 50)];
    }
  },
  COMPANY_TYPE: {
    NAME: 'companyType',
    ID: 'company_type',
    LABEL: 'Company type',
    LABEL_DESCRIPTION: 'Your company type',
    REQUIRED: true,
    PLACEHOLDER: 'Company type',
    TYPE: FIELDS_TYPES.TYPE_SELECT,
    VALUES_KEY: OPTION_KEYS.COMPANY_TYPES
  },
  COUNTRY: {
    NAME: 'country',
    ID: 'country',
    LABEL: 'Country',
    LABEL_DESCRIPTION: 'Location by country',
    REQUIRED: true,
    PLACEHOLDER: 'Country',
    TYPE: FIELDS_TYPES.TYPE_SELECT,
    VALUES_KEY: OPTION_KEYS.PERSONAL_COUNTRIES
  },
  CITY: {
    NAME: 'city',
    ID: 'city',
    LABEL: 'City',
    LABEL_DESCRIPTION: 'Location by city',
    REQUIRED: true,
    PLACEHOLDER: 'City',
    TYPE: FIELDS_TYPES.TYPE_TEXT,
    SHOW_MESSAGES_ON_INPUT_VALUE: true,
    VALIDATE: (value = '') => {
      return [generateMessage('City maximum text length is 20', value.length <= 20)];
    }
  },
  PHONE_NUMBER: {
    NAME: 'phoneNumber',
    ID: 'phone_number',
    LABEL: 'Phone number',
    LABEL_DESCRIPTION: 'Contact phone number',
    REQUIRED: true,
    PLACEHOLDER: 'Phone number',
    TYPE: FIELDS_TYPES.TYPE_TEXT,
    FORMAT: (value = '') => {
      if (value === '+') {
        return '+';
      }

      const pattern = /\d+/g;
      const result = value.match(pattern);
      if (!result) {
        return '+';
      }

      let formatted = result.join('');
      while (formatted.startsWith('0')) {
        formatted = formatted.slice(1);
      }

      return '+' + formatted;
    }
    // VALIDATE: (value = '') => {
    //   return [
    //     generateMessage(
    //       'Please enter valid phone number',
    //       phone(value, { strictDetection: true }).isValid
    //     )
    //   ];
    // }
  },
  CONTACT_EMAIL: {
    NAME: 'contactEmail',
    ID: 'contact_email',
    LABEL: 'Contact email',
    LABEL_DESCRIPTION: 'Legal email address',
    REQUIRED: true,
    PLACEHOLDER: 'Email address',
    TYPE: FIELDS_TYPES.TYPE_EMAIL,
    VALIDATE: (value = '') => {
      return [
        generateMessage(
          'Please use your company contact email address',
          isUsingCompanyEmail(value)
        ),
        generateMessage('Email maximum text length is 254', value.length <= 254)
      ];
    }
  },
  TAGLINE: {
    NAME: 'tagline',
    ID: 'tagline',
    LABEL: 'Tagline',
    LABEL_DESCRIPTION: 'Your preferred slogan',
    REQUIRED: false,
    PLACEHOLDER: 'Tagline',
    TYPE: FIELDS_TYPES.TYPE_TEXT,
    SHOW_MESSAGES_ON_INPUT_VALUE: true,
    VALIDATE: (value = '') => {
      return [generateMessage('Tagline maximum text length is 100', value.length <= 100)];
    }
  }
};

export const FUND_SEARCH_CATEGORY_FIELDS = {
  SEARCH_CATEGORY: {
    NAME: 'searchCategory',
    ID: 'search_category_funds',
    LABEL: 'Search type',
    REQUIRED: true,
    HIDE_REQUIRED_LABEL_SYMBOL: true,
    TYPE: FIELDS_TYPES.TYPE_RADIO,
    WRAP_CLASSNAME: 'form-group radio-row',
    VALUES_KEY: UI_OPTION_KEYS.FUND_SEARCH_CATEGORY
  },
  SEARCH_CATEGORY_MOBILE: {
    NAME: 'searchCategory',
    ID: 'search_category_funds_mobile',
    LABEL: 'Search type',
    HIDE_REQUIRED_LABEL_SYMBOL: true,
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_SELECT,
    VALUES_KEY: UI_OPTION_KEYS.FUND_SEARCH_CATEGORY
  }
};

export const PORTFOLIO_SEARCH_CATEGORY_FIELDS = {
  SEARCH_CATEGORY: {
    NAME: 'searchCategory',
    ID: 'search_category_portfolios',
    LABEL: 'Search type',
    REQUIRED: true,
    HIDE_REQUIRED_LABEL_SYMBOL: true,
    TYPE: FIELDS_TYPES.TYPE_RADIO,
    WRAP_CLASSNAME: 'form-group radio-row',
    VALUES_KEY: UI_OPTION_KEYS.PORTFOLIO_SEARCH_CATEGORY
  },
  SEARCH_CATEGORY_MOBILE: {
    NAME: 'searchCategory',
    ID: 'search_category_portfolios_mobile',
    LABEL: 'Search type',
    HIDE_REQUIRED_LABEL_SYMBOL: true,
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_SELECT,
    VALUES_KEY: UI_OPTION_KEYS.PORTFOLIO_SEARCH_CATEGORY
  }
};

export const PROFILER_SEARCH_CATEGORY_FIELDS = {
  SEARCH_CATEGORY: {
    NAME: 'searchCategory',
    ID: 'search_category_profiler',
    LABEL: 'Search type',
    REQUIRED: true,
    HIDE_REQUIRED_LABEL_SYMBOL: true,
    TYPE: FIELDS_TYPES.TYPE_RADIO,
    WRAP_CLASSNAME: 'form-group radio-row',
    VALUES_KEY: UI_OPTION_KEYS.PROFILER_SEARCH_CATEGORY
  },
  SEARCH_CATEGORY_MOBILE: {
    NAME: 'searchCategory',
    ID: 'search_category_profiler_mobile',
    LABEL: 'Search type',
    HIDE_REQUIRED_LABEL_SYMBOL: true,
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_SELECT,
    VALUES_KEY: UI_OPTION_KEYS.PROFILER_SEARCH_CATEGORY
  }
};

export const INDIVIDUALS_SEARCH_CATEGORY_FIELDS = {
  SEARCH_CATEGORY: {
    NAME: 'searchCategory',
    ID: 'search_category_individuals',
    LABEL: 'Search type',
    REQUIRED: true,
    HIDE_REQUIRED_LABEL_SYMBOL: true,
    TYPE: FIELDS_TYPES.TYPE_RADIO,
    WRAP_CLASSNAME: 'form-group radio-row',
    VALUES_KEY: UI_OPTION_KEYS.INDIVIDUALS_SEARCH_CATEGORY
  },
  SEARCH_CATEGORY_MOBILE: {
    NAME: 'searchCategory',
    ID: 'search_category_individuals_mobile',
    LABEL: 'Search type',
    HIDE_REQUIRED_LABEL_SYMBOL: true,
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_SELECT,
    VALUES_KEY: UI_OPTION_KEYS.INDIVIDUALS_SEARCH_CATEGORY
  }
};

export const SEARCH_FIELDS = {
  SEARCH_TYPE: {
    NAME: 'searchType',
    ID: 'search_type',
    LABEL: 'Profile',
    PLACEHOLDER: 'e.g. Funds',
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_RADIO,
    WRAP_CLASSNAME: 'form-group radio-row',
    VALUES_KEY: UI_OPTION_KEYS.SEARCH_TYPE
  },
  SEARCH_TYPE_MOBILE: {
    NAME: 'searchType',
    ID: 'search_type_mobile',
    LABEL: 'Profile',
    HIDE_REQUIRED_LABEL_SYMBOL: true,
    PLACEHOLDER: 'Profile',
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_SELECT,
    VALUES_KEY: UI_OPTION_KEYS.SEARCH_TYPE
  },
  INDUSTRY: {
    SECTION: SEARCH_CATEGORIES.GENERAL,
    NAME: 'industries',
    ID: 'search_industry',
    LABEL: 'Industry',
    PLACEHOLDER: 'Select',
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_MULTI_SELECT,
    VALUES_KEY: OPTION_KEYS.INDUSTRIES
  },
  HEADQUARTER: {
    SECTION: SEARCH_CATEGORIES.GENERAL,
    NAME: 'headquarter',
    ID: 'search_headquarter',
    LABEL: 'Headquarters',
    PLACEHOLDER: 'Select',
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_SELECT,
    VALUES_KEY: OPTION_KEYS.COUNTRIES
  },
  GEOGRAPHICAL_FOCUS: {
    SECTION: SEARCH_CATEGORIES.GENERAL,
    NAME: 'geographies',
    ID: 'search_geographical_focus',
    LABEL: 'Geo focus',
    PLACEHOLDER: 'Select',
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_MULTI_SELECT,
    VALUES_KEY: OPTION_KEYS.GEOGRAPHIES
  },
  REVENUE: {
    SECTION: SEARCH_CATEGORIES.FINANCIALS,
    NAME: 'revenue',
    ID: 'search_revenue',
    LABEL: 'Sales (€M)',
    LABEL_COMPACT: 'Sales',
    PLACEHOLDER: 'Value',
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_NUMBER
  },
  EBITDA: {
    SECTION: SEARCH_CATEGORIES.FINANCIALS,
    NAME: 'ebitda',
    ID: 'search_ebitda',
    LABEL: 'EBITDA (€M)',
    LABEL_COMPACT: 'EBITDA',
    PLACEHOLDER: 'Value',
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_NUMBER
  },
  EBITDA_MARGIN: {
    SECTION: SEARCH_CATEGORIES.FINANCIALS,
    NAME: 'ebitdaMargin',
    ID: 'search_ebitda_margin',
    LABEL: 'EBITDA Margin (%)',
    LABEL_COMPACT: 'EBITDA Margin',
    PLACEHOLDER: 'Value',
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_NUMBER
  },
  EQUITY_STAKE: {
    SECTION: SEARCH_CATEGORIES.TRANSACTION,
    NAME: 'equityStakes',
    ID: 'search_equity_stake',
    LABEL: 'Equity stake',
    PLACEHOLDER: 'Select',
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_MULTI_SELECT,
    VALUES_KEY: OPTION_KEYS.EQUITY_STAKES
  },
  SITUATION: {
    SECTION: SEARCH_CATEGORIES.TRANSACTION,
    NAME: 'situation',
    ID: 'search_situation',
    LABEL: 'Situation',
    PLACEHOLDER: 'Select',
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_MULTI_SELECT,
    VALUES_KEY: OPTION_KEYS.SITUATIONS
  },
  ENTERPRISE_MIN: {
    SECTION: SEARCH_CATEGORIES.VALUATION,
    NAME: 'enterpriseMin',
    ID: 'search_enterprise_value_min',
    LABEL: 'Enterprise value range (€M)',
    LABEL_COMPACT: 'Enterprise value range',
    PLACEHOLDER: 'Min',
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    PAIR_NAME: 'enterpriseMax'
  },
  ENTERPRISE_MAX: {
    // SECTION: SEARCH_CATEGORIES.VALUATION,
    NAME: 'enterpriseMax',
    ID: 'search_enterprise_value_max',
    PLACEHOLDER: 'Max',
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_NUMBER
  }
};

export const SEARCH_FIELDS_PORTFOLIO_INVESTMENT_CRITERIA = {
  SEARCH_TYPE: {
    NAME: 'searchType',
    ID: 'search_type_portfolios',
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_RADIO,
    VALUES_KEY: UI_OPTION_KEYS.SEARCH_TYPE
  },
  INDUSTRY: {
    NAME: 'industries',
    ID: 'search_industry_portfolio_investment_criteria',
    LABEL: 'Industry',
    PLACEHOLDER: 'Select',
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_MULTI_SELECT,
    VALUES_KEY: OPTION_KEYS.INDUSTRIES
  },
  HEADQUARTER: {
    NAME: 'headquarter',
    ID: 'search_headquarter_portfolio_investment_criteria',
    LABEL: 'Headquarters',
    PLACEHOLDER: 'Select',
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_SELECT,
    VALUES_KEY: OPTION_KEYS.COUNTRIES
  },
  GEOGRAPHICAL_FOCUS: {
    NAME: 'geographies',
    ID: 'search_geographical_focus',
    LABEL: 'Geo focus',
    PLACEHOLDER: 'Select',
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_MULTI_SELECT,
    VALUES_KEY: OPTION_KEYS.GEOGRAPHIES
  },
  REVENUE: {
    NAME: 'revenue',
    ID: 'search_revenue',
    LABEL: 'Sales (€M)',
    LABEL_COMPACT: 'Sales',
    PLACEHOLDER: 'Value',
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_NUMBER
  }
};

export const OUTREACH_CONTACT_FIELDS = {
  FIRST_NAME: {
    NAME: 'firstName',
    ID: 'outreach_contact_first_name',
    LABEL: 'First name',
    PLACEHOLDER: 'First name',
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_TEXT,
    VALIDATE: (value = '') => {
      return [generateMessage('First name maximum text length is 25', value.length <= 25)];
    }
  },
  LAST_NAME: {
    NAME: 'lastName',
    ID: 'outreach_contact_last_name',
    LABEL: 'Last name',
    PLACEHOLDER: 'Last name',
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_TEXT,
    VALIDATE: (value = '') => {
      return [generateMessage('Last name maximum text length is 25', value.length <= 25)];
    }
  },
  EMAIL: {
    NAME: 'email',
    ID: 'outreach_contact_email',
    LABEL: 'Email',
    PLACEHOLDER: 'Email',
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_TEXT,
    FORMAT: (value = '') => {
      return value.match(/^[a-zA-Z0-9!#$%&'*+/=?^_`{|}~.-]+/)?.[0] || '';
    }
  }
};

export const OUTREACH_FIELDS = {
  SUBJECT: {
    NAME: 'subject',
    ID: 'subject',
    LABEL: 'Subject',
    PLACEHOLDER: 'Subject',
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_TEXT,
    SHOW_MESSAGES_ON_INPUT_VALUE: true,
    VALIDATE: (value = '') => {
      return [generateMessage('Subject maximum text length is 150', value.length <= 150)];
    }
  },
  MESSAGE: {
    NAME: 'message',
    ID: 'message',
    LABEL: 'Description',
    PLACEHOLDER: 'Provide your message here',
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_TEXTAREA,
    SHOW_MESSAGES_ON_INPUT_VALUE: true,
    VALIDATE: (value = '') => {
      return [generateMessage('Message maximum text length is 500', value.length <= 500)];
    }
  }
};

export const MASS_OUTREACH_FIELDS = {
  ENRIES_IDS: {
    NAME: 'entriesIds',
    ID: 'entries_ids',
    LABEL: 'Select investors',
    PLACEHOLDER: 'Select investors',
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_MULTI_SELECT
  },
  ...OUTREACH_FIELDS
};

export const OUTREACH_SEND_MESSAGE_FIELDS = {
  MESSAGE: {
    NAME: 'message',
    ID: 'outreach_send_message',
    PLACEHOLDER: 'Type a message here',
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_TEXTAREA,
    SHOW_MESSAGES_ON_INPUT_VALUE: true,
    VALIDATE: (value = '') => {
      return [generateMessage('Message maximum text length is 2000', value.length <= 2000)];
    }
  }
};

export const FORWARD_OUTREACH_FIELDS = {
  USER: {
    NAME: 'user',
    ID: 'user_id',
    LABEL: 'Colleague name',
    PLACEHOLDER: 'Select',
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_SELECT
  },
  ENTRY: {
    NAME: 'entry',
    ID: 'entry_id',
    LABEL: 'Investment vehicle',
    PLACEHOLDER: 'Select',
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_SELECT
  }
};

export const REQUEST_FEEDBACK_OUTREACH_FIELDS = {
  USERS: {
    NAME: 'users',
    ID: 'users_id',
    LABEL: 'Colleague name(s)',
    PLACEHOLDER: 'Select',
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_MULTI_SELECT
  },
  MESSAGE: {
    NAME: 'message',
    ID: 'outreach_feedback_message',
    LABEL: 'Message',
    PLACEHOLDER: 'Provide a briefing for your feedback request',
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_TEXTAREA,
    SHOW_MESSAGES_ON_INPUT_VALUE: true,
    VALIDATE: (value = '') => {
      return [generateMessage('Message maximum text length is 2000', value.length <= 2000)];
    }
  }
};

export const FEEDBACK_FIELDS = {
  COLLEAGUES: {
    NAME: 'colleagues',
    ID: 'colleagues',
    LABEL: 'Colleagues',
    PLACEHOLDER: 'Search colleagues',
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_MULTI_SELECT
  },
  NOTE: {
    NAME: 'note',
    ID: 'note',
    LABEL: 'Message',
    PLACEHOLDER: 'e.g. Write any help text here',
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_TEXTAREA,
    SHOW_MESSAGES_ON_INPUT_VALUE: true,
    VALIDATE: (value = '') => {
      return [generateMessage('Message maximum text length is 500', value.length <= 500)];
    }
  }
};

export const FEEDBACK_PROFILER_FIELDS = {
  COLLEAGUES: {
    NAME: 'colleagues',
    ID: 'colleagues',
    LABEL: 'Colleagues',
    PLACEHOLDER: 'Search colleagues',
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_MULTI_SELECT
  },
  NOTE: {
    NAME: 'note',
    ID: 'note',
    LABEL: 'Message',
    PLACEHOLDER: 'e.g. Write any help text here',
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_TEXTAREA,
    SHOW_MESSAGES_ON_INPUT_VALUE: true,
    VALIDATE: (value = '') => {
      return [generateMessage('Message maximum text length is 500', value.length <= 500)];
    }
  }
};

export const PROVIDE_FEEDBACK_FIELDS = {
  COMMENT: {
    NAME: 'comment',
    ID: 'comment',
    LABEL: 'Feedback general comment',
    PLACEHOLDER: 'Write your comment here',
    REQUIRED: false,
    TYPE: FIELDS_TYPES.TYPE_TEXTAREA,
    SHOW_MESSAGES_ON_INPUT_VALUE: true,
    VALIDATE: (value = '') => {
      return [generateMessage('Message maximum text length is 500', value.length <= 500)];
    }
  }
};

export const PROFILER_PROVIDE_FEEDBACK_FIELDS = {
  COMMENT: {
    NAME: 'comment',
    ID: 'comment',
    LABEL: 'Feedback general comment',
    PLACEHOLDER: 'Write your comment here',
    REQUIRED: false,
    TYPE: FIELDS_TYPES.TYPE_TEXTAREA,
    SHOW_MESSAGES_ON_INPUT_VALUE: true,
    VALIDATE: (value = '') => {
      return [generateMessage('Message maximum text length is 500', value.length <= 500)];
    }
  }
};

export const FUND_FIELDS = {
  NAME: {
    NAME: 'name',
    ID: 'fund_name',
    LABEL: 'Fund name',
    LABEL_DESCRIPTION: 'Official name of fund',
    REQUIRED: true,
    PLACEHOLDER: 'Fund name',
    TYPE: FIELDS_TYPES.TYPE_TEXT,
    SHOW_MESSAGES_ON_INPUT_VALUE: true,
    VALIDATE: (value = '') => {
      return [generateMessage('Fund name maximum text length is 30', value.length <= 30)];
    },
    FUND_VIEW_TAB: FUNDS_VIEW_TABS.INFORMATION,
    GA4: {
      [GA_EVENTS.fund_create_Information_complete]: GA_DIMENSIONS.fund_name,
      [GA_EVENTS.fund_create_GeoIndustries_complete]: GA_DIMENSIONS.fund_name,
      [GA_EVENTS.fund_create_financials_complete]: GA_DIMENSIONS.fund_name,
      [GA_EVENTS.fund_create_transaction_complete]: GA_DIMENSIONS.fund_name,
      [GA_EVENTS.fund_create_valuation_complete]: GA_DIMENSIONS.fund_name,
      [GA_EVENTS.fund_create_complete]: GA_DIMENSIONS.fund_name
    }
  },
  VINTAGE: {
    NAME: 'vintage',
    ID: 'fund_vintage',
    LABEL: 'Fund vintage',
    LABEL_DESCRIPTION: 'Year of initial investments',
    PLACEHOLDER: 'Fund vintage',
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    INTEGER: true,
    MAXLENGTH: 4,
    MIN: 2000,
    VALIDATE: (value = '') => {
      const [min, max] = [2000, new Date().getFullYear()];
      let isValid = true;
      if (value.length === 0) {
        isValid = false;
      } else {
        const num = +value;
        if (num > max || num < min) {
          isValid = false;
        }
      }
      return [generateMessage(`Fund vintage must be between ${min} and ${max}`, isValid)];
    },
    FUND_VIEW_TAB: FUNDS_VIEW_TABS.INFORMATION,
    GA4: {
      [GA_EVENTS.fund_create_Information_complete]: GA_DIMENSIONS.Fund_vintage
    }
  },
  VOLUME: {
    NAME: 'volume',
    ID: 'fund_volume',
    LABEL: 'Fund volume',
    LABEL_DESCRIPTION: 'Total volume (€M)',
    PLACEHOLDER: 'Fund volume',
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    FUND_VIEW_TAB: FUNDS_VIEW_TABS.INFORMATION,
    GA4: {
      [GA_EVENTS.fund_create_Information_complete]: GA_DIMENSIONS.Fund_Volume
    }
  },
  DESCRIPTION: {
    NAME: 'description',
    ID: 'fund_description',
    LABEL: 'Fund description',
    LABEL_DESCRIPTION: 'Further relevant information',
    REQUIRED: true,
    PLACEHOLDER: 'Provide your description here',
    TYPE: FIELDS_TYPES.TYPE_TEXTAREA,
    WRAP_CLASSNAME: 'form-group contains-textarea',
    SHOW_MESSAGES_ON_INPUT_VALUE: true,
    VALIDATE: (value = '') => {
      return [generateMessage('Fund description maximum text length is 300', value.length <= 300)];
    },
    FUND_VIEW_TAB: FUNDS_VIEW_TABS.INFORMATION
  },
  GEOGRAPHIES: {
    NAME: 'geography',
    ID: 'fund_geography',
    LABEL: 'Geographies',
    PLACEHOLDER: 'Geographical focus',
    LABEL_DESCRIPTION: 'Geographical focus',
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_MULTI_SELECT,
    VALUES_KEY: OPTION_KEYS.GEOGRAPHIES,
    FUND_VIEW_TAB: FUNDS_VIEW_TABS.GEO_INDUSTRIES,
    GA4: {
      [GA_EVENTS.fund_create_GeoIndustries_complete]: GA_DIMENSIONS.Geographies
    }
  },
  INDUSTRIES: {
    NAME: 'industries',
    ID: 'fund_industries',
    LABEL: 'Industries',
    PLACEHOLDER: 'Industries',
    LABEL_DESCRIPTION: 'Focus industries',
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_MULTI_SELECT,
    VALUES_KEY: OPTION_KEYS.INDUSTRIES,
    FUND_VIEW_TAB: FUNDS_VIEW_TABS.GEO_INDUSTRIES,
    GA4: {
      [GA_EVENTS.fund_create_GeoIndustries_complete]: GA_DIMENSIONS.Industries
    }
  },
  SITUATION: {
    NAME: 'situations',
    ID: 'fund_situation',
    LABEL: 'Situation',
    LABEL_DESCRIPTION: 'Business situation',
    REQUIRED: true,
    PLACEHOLDER: 'Situation',
    TYPE: FIELDS_TYPES.TYPE_MULTI_SELECT,
    VALUES_KEY: OPTION_KEYS.SITUATIONS,
    FUND_VIEW_TAB: FUNDS_VIEW_TABS.TRANSACTION_TYPES,
    GA4: {
      [GA_EVENTS.fund_create_transaction_complete]: GA_DIMENSIONS.Situation
    }
  },
  EQUITY: {
    NAME: 'equityStakes',
    ID: 'fund_equity',
    LABEL: 'Equity stake',
    LABEL_DESCRIPTION: 'Investment type',
    REQUIRED: true,
    PLACEHOLDER: 'Equity stake',
    TYPE: FIELDS_TYPES.TYPE_MULTI_SELECT,
    VALUES_KEY: OPTION_KEYS.EQUITY_STAKES,
    FUND_VIEW_TAB: FUNDS_VIEW_TABS.TRANSACTION_TYPES,
    GA4: {
      [GA_EVENTS.fund_create_transaction_complete]: GA_DIMENSIONS.Equity_Stake
    }
  },
  REVENUE_RANGE_MIN: {
    NAME: 'revenueMin',
    ID: 'fund_revenue_range_min',
    LABEL: 'Sales range',
    LABEL_DESCRIPTION: 'Target sales (€M)',
    REQUIRED: true,
    PLACEHOLDER: 'Min',
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    PAIR_NAME: 'revenueMax',
    FUND_VIEW_TAB: FUNDS_VIEW_TABS.FINANCIAL_METRICS,
    GA4: {
      [GA_EVENTS.fund_create_financials_complete]: GA_DIMENSIONS.Revenue_Range_min
    }
  },
  REVENUE_RANGE_MAX: {
    NAME: 'revenueMax',
    ID: 'fund_revenue_range_max',
    PLACEHOLDER: 'Max',
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    REQUIRED: true,
    FUND_VIEW_TAB: FUNDS_VIEW_TABS.FINANCIAL_METRICS,
    GA4: {
      [GA_EVENTS.fund_create_financials_complete]: GA_DIMENSIONS.Revenue_Range_max
    }
  },
  EBITDA_RANGE_MIN: {
    NAME: 'ebitdaMin',
    ID: 'fund_ebitda_range_min',
    LABEL: 'EBITDA range',
    LABEL_DESCRIPTION: 'Target EBITDA (€M)',
    PLACEHOLDER: 'Min',
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    REQUIRED: true,
    PAIR_NAME: 'ebitdaMax',
    FUND_VIEW_TAB: FUNDS_VIEW_TABS.FINANCIAL_METRICS,
    GA4: {
      [GA_EVENTS.fund_create_financials_complete]: GA_DIMENSIONS.EBITDA_min
    }
  },
  EBITDA_RANGE_MAX: {
    NAME: 'ebitdaMax',
    ID: 'fund_ebitda_range_max',
    PLACEHOLDER: 'Max',
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    REQUIRED: true,
    FUND_VIEW_TAB: FUNDS_VIEW_TABS.FINANCIAL_METRICS,
    GA4: {
      [GA_EVENTS.fund_create_financials_complete]: GA_DIMENSIONS.EBITDA_max
    }
  },
  EBITDA_MARGIN: {
    NAME: 'ebitdaMargin',
    ID: 'fund_ebitda_margin',
    LABEL: 'EBITDA margin (min.)',
    LABEL_DESCRIPTION: 'Target margin (%)',
    PLACEHOLDER: 'Value %',
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    REQUIRED: true,
    FUND_VIEW_TAB: FUNDS_VIEW_TABS.FINANCIAL_METRICS,
    GA4: {
      [GA_EVENTS.fund_create_financials_complete]: GA_DIMENSIONS.EBITDA_margin
    }
  },
  EBIT_RANGE_MIN: {
    NAME: 'ebitMin',
    ID: 'fund_ebit_range_min',
    LABEL: 'EBIT range',
    LABEL_DESCRIPTION: 'Target EBIT (€M)',
    PLACEHOLDER: 'Min',
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    REQUIRED: true,
    PAIR_NAME: 'ebitMax',
    FUND_VIEW_TAB: FUNDS_VIEW_TABS.FINANCIAL_METRICS,
    GA4: {
      [GA_EVENTS.fund_create_financials_complete]: GA_DIMENSIONS.EBIT_min
    }
  },
  EBIT_RANGE_MAX: {
    NAME: 'ebitMax',
    ID: 'fund_ebit_range_max',
    PLACEHOLDER: 'Max',
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    REQUIRED: true,
    FUND_VIEW_TAB: FUNDS_VIEW_TABS.FINANCIAL_METRICS,
    GA4: {
      [GA_EVENTS.fund_create_financials_complete]: GA_DIMENSIONS.EBIT_max
    }
  },
  EBIT_MARGIN: {
    NAME: 'ebitMargin',
    ID: 'fund_ebit_margin',
    LABEL: 'EBIT margin (min.)',
    LABEL_DESCRIPTION: 'Target margin (%)',
    PLACEHOLDER: 'Value %',
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    REQUIRED: true,
    FUND_VIEW_TAB: FUNDS_VIEW_TABS.FINANCIAL_METRICS,
    GA4: {
      [GA_EVENTS.fund_create_financials_complete]: GA_DIMENSIONS.EBIT_margin
    }
  },
  ENTERPRISE_RANGE_MIN: {
    NAME: 'enterpriseMin',
    ID: 'fund_enterprise_value_range_min',
    LABEL: 'Enterprise value range',
    LABEL_DESCRIPTION: 'Target value (€M)',
    PLACEHOLDER: 'Min',
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    REQUIRED: true,
    PAIR_NAME: 'enterpriseMax',
    FUND_VIEW_TAB: FUNDS_VIEW_TABS.VALUATION,
    GA4: {
      [GA_EVENTS.fund_create_valuation_complete]: GA_DIMENSIONS.Enterprise_VR_min
    }
  },
  ENTERPRISE_RANGE_MAX: {
    NAME: 'enterpriseMax',
    ID: 'fund_enterprise_value_range_max',
    PLACEHOLDER: 'Max',
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    REQUIRED: true,
    FUND_VIEW_TAB: FUNDS_VIEW_TABS.VALUATION,
    GA4: {
      [GA_EVENTS.fund_create_valuation_complete]: GA_DIMENSIONS.Enterprise_VR_max
    }
  },
  EQUITY_RANGE_MIN: {
    NAME: 'equityMin',
    ID: 'fund_equity_value_range_min',
    LABEL: 'Equity value range',
    LABEL_DESCRIPTION: 'Target value (€M)',
    PLACEHOLDER: 'Min',
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    REQUIRED: true,
    PAIR_NAME: 'equityMax',
    FUND_VIEW_TAB: FUNDS_VIEW_TABS.VALUATION,
    GA4: {
      [GA_EVENTS.fund_create_valuation_complete]: GA_DIMENSIONS.Equity_VR_min
    }
  },
  EQUITY_RANGE_MAX: {
    NAME: 'equityMax',
    ID: 'fund_equity_value_range_max',
    PLACEHOLDER: 'Max',
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    REQUIRED: true,
    FUND_VIEW_TAB: FUNDS_VIEW_TABS.VALUATION,
    GA4: {
      [GA_EVENTS.fund_create_valuation_complete]: GA_DIMENSIONS.Equity_VR_max
    }
  }
};

export const PORTFOLIO_FIELDS = {
  NAME: {
    NAME: 'name',
    ID: 'portfolio_name',
    LABEL: 'Portfolio company name',
    LABEL_DESCRIPTION: 'Official name of the portfolio',
    REQUIRED: true,
    PLACEHOLDER: 'Portfolio co. name',
    TYPE: FIELDS_TYPES.TYPE_TEXT,
    SHOW_MESSAGES_ON_INPUT_VALUE: true,
    VALIDATE: (value = '') => {
      return [generateMessage('Portfolio name maximum text length is 30', value.length <= 30)];
    },
    PORTFOLIO_VIEW_TAB: PORTFOLIOS_VIEW_TABS.INFORMATION,
    GA4: {
      [GA_EVENTS.portfolio_co_information_complete]: GA_DIMENSIONS.portfolio_co_name,
      [GA_EVENTS.portfolio_co_geoindustries_complete]: GA_DIMENSIONS.portfolio_co_name,
      [GA_EVENTS.portfolio_co_financials_complete]: GA_DIMENSIONS.portfolio_co_name,
      [GA_EVENTS.portfolio_co_transaction_complete]: GA_DIMENSIONS.portfolio_co_name,
      [GA_EVENTS.portfolio_co_valuation_complete]: GA_DIMENSIONS.portfolio_co_name,
      [GA_EVENTS.portfolio_co_create_complete]: GA_DIMENSIONS.portfolio_co_name
    }
  },
  REVENUE: {
    NAME: 'revenue',
    ID: 'portfolio_revenue',
    LABEL: 'Portfolio company sales',
    LABEL_DESCRIPTION: 'Sales (€M)',
    PLACEHOLDER: 'Portfolio co. sales',
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    PORTFOLIO_VIEW_TAB: PORTFOLIOS_VIEW_TABS.INFORMATION,
    GA4: {
      [GA_EVENTS.portfolio_co_information_complete]: GA_DIMENSIONS.portfolio_company_revenue
    }
  },
  ACQUISITION_DATE: {
    NAME: 'acquisitionDate',
    ID: 'portfolio_acquisition_date',
    LABEL: 'Portfolio company acquisition date',
    LABEL_DESCRIPTION: 'Year of acquisition',
    REQUIRED: true,
    PLACEHOLDER: 'Acquisition date',
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    INTEGER: true,
    MAXLENGTH: 4,
    MIN: 2000,
    VALIDATE: (value = '') => {
      const [min, max] = [2000, new Date().getFullYear()];
      let isValid = true;
      if (value.length === 0) {
        isValid = false;
      } else {
        const num = +value;
        if (num > max || num < min) {
          isValid = false;
        }
      }
      return [generateMessage(`Acquisition date must be between ${min} and ${max}`, isValid)];
    },
    PORTFOLIO_VIEW_TAB: PORTFOLIOS_VIEW_TABS.INFORMATION
  },
  EBITDA: {
    NAME: 'ebitda',
    ID: 'portfolio_ebitda',
    LABEL: 'Portfolio company EBITDA',
    LABEL_DESCRIPTION: 'EBITDA (€M)',
    PLACEHOLDER: 'Portfolio co. EBITDA',
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    PORTFOLIO_VIEW_TAB: PORTFOLIOS_VIEW_TABS.INFORMATION,
    GA4: {
      [GA_EVENTS.portfolio_co_information_complete]: GA_DIMENSIONS.portfolio_company_EBITDA
    }
  },
  DESCRIPTION: {
    NAME: 'description',
    ID: 'portfolio_description',
    LABEL: 'Portfolio company description',
    LABEL_DESCRIPTION: 'Further relevant information',
    REQUIRED: true,
    PLACEHOLDER: 'Provide your description here',
    TYPE: FIELDS_TYPES.TYPE_TEXTAREA,
    WRAP_CLASSNAME: 'form-group contains-textarea',
    SHOW_MESSAGES_ON_INPUT_VALUE: true,
    VALIDATE: (value = '') => {
      return [
        generateMessage('Portfolio description maximum text length is 300', value.length <= 300)
      ];
    },
    PORTFOLIO_VIEW_TAB: PORTFOLIOS_VIEW_TABS.INFORMATION,
    GA4: {
      [GA_EVENTS.portfolio_co_information_complete]: GA_DIMENSIONS.portfolio_company_description
    }
  },
  AFFILIATED_FUND: {
    NAME: 'affiliateFund',
    ID: 'portfolio_affiliateFund',
    LABEL: 'Affiliated fund',
    LABEL_DESCRIPTION: 'Allocation to fund',
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_SELECT,
    PORTFOLIO_VIEW_TAB: PORTFOLIOS_VIEW_TABS.INFORMATION
  },
  GEOGRAPHIES: {
    NAME: 'geography',
    ID: 'portfolio_geographies',
    LABEL: 'Geographies',
    PLACEHOLDER: 'Geographical focus',
    LABEL_DESCRIPTION: 'Geographical focus',
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_MULTI_SELECT,
    VALUES_KEY: OPTION_KEYS.GEOGRAPHIES,
    PORTFOLIO_VIEW_TAB: PORTFOLIOS_VIEW_TABS.GEO_INDUSTRIES,
    GA4: {
      [GA_EVENTS.portfolio_co_geoindustries_complete]: GA_DIMENSIONS.geographies
    }
  },
  INDUSTRIES: {
    NAME: 'industries',
    ID: 'portfolio_industries',
    LABEL: 'Industries',
    PLACEHOLDER: 'Industries',
    LABEL_DESCRIPTION: 'Focus industries',
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_MULTI_SELECT,
    VALUES_KEY: OPTION_KEYS.INDUSTRIES,
    PORTFOLIO_VIEW_TAB: PORTFOLIOS_VIEW_TABS.GEO_INDUSTRIES,
    GA4: {
      [GA_EVENTS.portfolio_co_geoindustries_complete]: GA_DIMENSIONS.industries
    }
  },
  SITUATION: {
    NAME: 'situations',
    ID: 'portfolio_situation',
    LABEL: 'Situation',
    LABEL_DESCRIPTION: 'Business situation',
    REQUIRED: true,
    PLACEHOLDER: 'Situation',
    TYPE: FIELDS_TYPES.TYPE_MULTI_SELECT,
    VALUES_KEY: OPTION_KEYS.SITUATIONS,
    PORTFOLIO_VIEW_TAB: PORTFOLIOS_VIEW_TABS.TRANSACTION_TYPES,
    GA4: {
      [GA_EVENTS.portfolio_co_transaction_complete]: GA_DIMENSIONS.situation
    }
  },
  EQUITY: {
    NAME: 'equityStakes',
    ID: 'portfolio_equity',
    LABEL: 'Equity stake',
    LABEL_DESCRIPTION: 'Investment type',
    REQUIRED: true,
    PLACEHOLDER: 'Equity stake',
    TYPE: FIELDS_TYPES.TYPE_MULTI_SELECT,
    VALUES_KEY: OPTION_KEYS.EQUITY_STAKES,
    PORTFOLIO_VIEW_TAB: PORTFOLIOS_VIEW_TABS.TRANSACTION_TYPES,
    GA4: {
      [GA_EVENTS.portfolio_co_transaction_complete]: GA_DIMENSIONS.equity_stake
    }
  },
  REVENUE_RANGE_MIN: {
    NAME: 'revenueMin',
    ID: 'portfolio_revenue_range_min',
    LABEL: 'Sales range',
    LABEL_DESCRIPTION: 'Target sales (€M)',
    REQUIRED: true,
    PLACEHOLDER: 'Min',
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    PAIR_NAME: 'revenueMax',
    PORTFOLIO_VIEW_TAB: PORTFOLIOS_VIEW_TABS.FINANCIAL_METRICS,
    GA4: {
      [GA_EVENTS.portfolio_co_financials_complete]: GA_DIMENSIONS.revenue_range_min
    }
  },
  REVENUE_RANGE_MAX: {
    NAME: 'revenueMax',
    ID: 'portfolio_revenue_range_max',
    PLACEHOLDER: 'Max',
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    REQUIRED: true,
    PORTFOLIO_VIEW_TAB: PORTFOLIOS_VIEW_TABS.FINANCIAL_METRICS,
    GA4: {
      [GA_EVENTS.portfolio_co_financials_complete]: GA_DIMENSIONS.revenue_range_max
    }
  },
  EBITDA_RANGE_MIN: {
    NAME: 'ebitdaMin',
    ID: 'portfolio_ebitda_range_min',
    LABEL: 'EBITDA range',
    LABEL_DESCRIPTION: 'Target EBITDA (€M)',
    PLACEHOLDER: 'Min',
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    REQUIRED: true,
    PAIR_NAME: 'ebitdaMax',
    PORTFOLIO_VIEW_TAB: PORTFOLIOS_VIEW_TABS.FINANCIAL_METRICS,
    GA4: {
      [GA_EVENTS.portfolio_co_financials_complete]: GA_DIMENSIONS.EBITDA_range_min
    }
  },
  EBITDA_RANGE_MAX: {
    NAME: 'ebitdaMax',
    ID: 'portfolio_ebitda_range_max',
    PLACEHOLDER: 'Max',
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    REQUIRED: true,
    PORTFOLIO_VIEW_TAB: PORTFOLIOS_VIEW_TABS.FINANCIAL_METRICS,
    GA4: {
      [GA_EVENTS.portfolio_co_financials_complete]: GA_DIMENSIONS.EBITDA_range_max
    }
  },
  EBITDA_MARGIN: {
    NAME: 'ebitdaMargin',
    ID: 'portfolio_ebitda_margin',
    LABEL: 'EBITDA margin (min.)',
    LABEL_DESCRIPTION: 'Target margin (%)',
    PLACEHOLDER: 'Value %',
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    REQUIRED: true,
    PORTFOLIO_VIEW_TAB: PORTFOLIOS_VIEW_TABS.FINANCIAL_METRICS,
    GA4: {
      [GA_EVENTS.portfolio_co_financials_complete]: GA_DIMENSIONS.EBITDA_margin
    }
  },
  EBIT_RANGE_MIN: {
    NAME: 'ebitMin',
    ID: 'portfolio_ebit_range_min',
    LABEL: 'EBIT range',
    LABEL_DESCRIPTION: 'Target EBIT (€M)',
    PLACEHOLDER: 'Min',
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    REQUIRED: true,
    PAIR_NAME: 'ebitMax',
    PORTFOLIO_VIEW_TAB: PORTFOLIOS_VIEW_TABS.FINANCIAL_METRICS,
    GA4: {
      [GA_EVENTS.portfolio_co_financials_complete]: GA_DIMENSIONS.EBIT_range_min
    }
  },
  EBIT_RANGE_MAX: {
    NAME: 'ebitMax',
    ID: 'portfolio_ebit_range_max',
    PLACEHOLDER: 'Max',
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    REQUIRED: true,
    PORTFOLIO_VIEW_TAB: PORTFOLIOS_VIEW_TABS.FINANCIAL_METRICS,
    GA4: {
      [GA_EVENTS.portfolio_co_financials_complete]: GA_DIMENSIONS.EBIT_range_max
    }
  },
  EBIT_MARGIN: {
    NAME: 'ebitMargin',
    ID: 'portfolio_ebit_margin',
    LABEL: 'EBIT margin (min.)',
    LABEL_DESCRIPTION: 'Target margin (%)',
    PLACEHOLDER: 'Value %',
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    REQUIRED: true,
    PORTFOLIO_VIEW_TAB: PORTFOLIOS_VIEW_TABS.FINANCIAL_METRICS,
    GA4: {
      [GA_EVENTS.portfolio_co_financials_complete]: GA_DIMENSIONS.EBIT_margin
    }
  },
  ENTERPRISE_RANGE_MIN: {
    NAME: 'enterpriseMin',
    ID: 'portfolio_enterprise_value_range_min',
    LABEL: 'Enterprise value range',
    LABEL_DESCRIPTION: 'Target value (€M)',
    PLACEHOLDER: 'Min',
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    REQUIRED: true,
    PAIR_NAME: 'enterpriseMax',
    PORTFOLIO_VIEW_TAB: PORTFOLIOS_VIEW_TABS.VALUATION,
    GA4: {
      [GA_EVENTS.portfolio_co_valuation_complete]: GA_DIMENSIONS.enterprise_value_range_min
    }
  },
  ENTERPRISE_RANGE_MAX: {
    NAME: 'enterpriseMax',
    ID: 'portfolio_enterprise_value_range_max',
    PLACEHOLDER: 'Max',
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    REQUIRED: true,
    PORTFOLIO_VIEW_TAB: PORTFOLIOS_VIEW_TABS.VALUATION,
    GA4: {
      [GA_EVENTS.portfolio_co_valuation_complete]: GA_DIMENSIONS.enterprise_value_range_max
    }
  },
  EQUITY_RANGE_MIN: {
    NAME: 'equityMin',
    ID: 'portfolio_equity_value_range_min',
    LABEL: 'Equity value range',
    LABEL_DESCRIPTION: 'Target value (€M)',
    PLACEHOLDER: 'Min',
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    REQUIRED: true,
    PAIR_NAME: 'equityMax',
    PORTFOLIO_VIEW_TAB: PORTFOLIOS_VIEW_TABS.VALUATION,
    GA4: {
      [GA_EVENTS.portfolio_co_valuation_complete]: GA_DIMENSIONS.equity_value_range_min
    }
  },
  EQUITY_RANGE_MAX: {
    NAME: 'equityMax',
    ID: 'portfolio_equity_value_range_max',
    PLACEHOLDER: 'Max',
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    REQUIRED: true,
    PORTFOLIO_VIEW_TAB: PORTFOLIOS_VIEW_TABS.VALUATION,
    GA4: {
      [GA_EVENTS.portfolio_co_valuation_complete]: GA_DIMENSIONS.equity_value_range_max
    }
  }
};

// TODO: Change fields and analytics data
export const INVEST_PROFILE_FIELDS = {
  NAME: {
    NAME: 'name',
    ID: 'profile_name',
    LABEL: 'Invest profile name',
    LABEL_DESCRIPTION: 'Name of your invest profile',
    REQUIRED: true,
    PLACEHOLDER: 'Invest profile name',
    TYPE: FIELDS_TYPES.TYPE_TEXT,
    SHOW_MESSAGES_ON_INPUT_VALUE: true,
    VALIDATE: (value = '') => {
      return [generateMessage('Profile name maximum text length is 30', value.length <= 30)];
    },
    INVEST_PROFILE_VIEW_TAB: INVEST_PROFILES_VIEW_TABS.INFORMATION,
    GA4: {
      [GA_EVENTS.portfolio_co_information_complete]: GA_DIMENSIONS.portfolio_co_name,
      [GA_EVENTS.portfolio_co_geoindustries_complete]: GA_DIMENSIONS.portfolio_co_name,
      [GA_EVENTS.portfolio_co_financials_complete]: GA_DIMENSIONS.portfolio_co_name,
      [GA_EVENTS.portfolio_co_transaction_complete]: GA_DIMENSIONS.portfolio_co_name,
      [GA_EVENTS.portfolio_co_valuation_complete]: GA_DIMENSIONS.portfolio_co_name,
      [GA_EVENTS.portfolio_co_create_complete]: GA_DIMENSIONS.portfolio_co_name
    }
  },
  REVENUE: {
    NAME: 'revenue',
    ID: 'profile_revenue',
    LABEL: 'Business unit sales',
    LABEL_DESCRIPTION: 'Sales (€M)',
    PLACEHOLDER: 'Business unit sales',
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    INVEST_PROFILE_VIEW_TAB: INVEST_PROFILES_VIEW_TABS.INFORMATION,
    GA4: {
      [GA_EVENTS.portfolio_co_information_complete]: GA_DIMENSIONS.portfolio_company_revenue
    }
  },
  EBITDA: {
    NAME: 'ebitda',
    ID: 'profile_ebitda',
    LABEL: 'Business unit EBITDA',
    LABEL_DESCRIPTION: 'EBITDA (€M)',
    PLACEHOLDER: 'Business unit EBITDA',
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    INVEST_PROFILE_VIEW_TAB: INVEST_PROFILES_VIEW_TABS.INFORMATION,
    GA4: {
      [GA_EVENTS.portfolio_co_information_complete]: GA_DIMENSIONS.portfolio_company_EBITDA
    }
  },
  DESCRIPTION: {
    NAME: 'description',
    ID: 'profile_description',
    LABEL: 'Business unit description',
    LABEL_DESCRIPTION: 'Further relevant information',
    REQUIRED: true,
    PLACEHOLDER: 'Provide your description here',
    TYPE: FIELDS_TYPES.TYPE_TEXTAREA,
    WRAP_CLASSNAME: 'form-group contains-textarea',
    SHOW_MESSAGES_ON_INPUT_VALUE: true,
    VALIDATE: (value = '') => {
      return [
        generateMessage('Business unit description maximum text length is 300', value.length <= 300)
      ];
    },
    INVEST_PROFILE_VIEW_TAB: INVEST_PROFILES_VIEW_TABS.INFORMATION,
    GA4: {
      [GA_EVENTS.portfolio_co_information_complete]: GA_DIMENSIONS.portfolio_company_description
    }
  },
  GEOGRAPHIES: {
    NAME: 'geography',
    ID: 'profile_geographies',
    LABEL: 'Geographies',
    PLACEHOLDER: 'Geographical focus',
    LABEL_DESCRIPTION: 'Geographical focus',
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_MULTI_SELECT,
    VALUES_KEY: OPTION_KEYS.GEOGRAPHIES,
    INVEST_PROFILE_VIEW_TAB: INVEST_PROFILES_VIEW_TABS.GEO_INDUSTRIES,
    GA4: {
      [GA_EVENTS.portfolio_co_geoindustries_complete]: GA_DIMENSIONS.geographies
    }
  },
  INDUSTRIES: {
    NAME: 'industries',
    ID: 'profile_industries',
    LABEL: 'Industries',
    PLACEHOLDER: 'Industries',
    LABEL_DESCRIPTION: 'Focus industries',
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_MULTI_SELECT,
    VALUES_KEY: OPTION_KEYS.INDUSTRIES,
    INVEST_PROFILE_VIEW_TAB: INVEST_PROFILES_VIEW_TABS.GEO_INDUSTRIES,
    GA4: {
      [GA_EVENTS.portfolio_co_geoindustries_complete]: GA_DIMENSIONS.industries
    }
  },
  SITUATION: {
    NAME: 'situations',
    ID: 'profile_situation',
    LABEL: 'Situation',
    LABEL_DESCRIPTION: 'Business situation',
    REQUIRED: true,
    PLACEHOLDER: 'Situation',
    TYPE: FIELDS_TYPES.TYPE_MULTI_SELECT,
    VALUES_KEY: OPTION_KEYS.SITUATIONS,
    INVEST_PROFILE_VIEW_TAB: INVEST_PROFILES_VIEW_TABS.TRANSACTION_TYPES,
    GA4: {
      [GA_EVENTS.portfolio_co_transaction_complete]: GA_DIMENSIONS.situation
    }
  },
  EQUITY: {
    NAME: 'equityStakes',
    ID: 'profile_equity',
    LABEL: 'Equity stake',
    LABEL_DESCRIPTION: 'Investment type',
    REQUIRED: true,
    PLACEHOLDER: 'Equity stake',
    TYPE: FIELDS_TYPES.TYPE_MULTI_SELECT,
    VALUES_KEY: OPTION_KEYS.EQUITY_STAKES,
    INVEST_PROFILE_VIEW_TAB: INVEST_PROFILES_VIEW_TABS.TRANSACTION_TYPES,
    GA4: {
      [GA_EVENTS.portfolio_co_transaction_complete]: GA_DIMENSIONS.equity_stake
    }
  },
  REVENUE_RANGE_MIN: {
    NAME: 'revenueMin',
    ID: 'profile_revenue_range_min',
    LABEL: 'Sales range',
    LABEL_DESCRIPTION: 'Target sales (€M)',
    REQUIRED: true,
    PLACEHOLDER: 'Min',
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    PAIR_NAME: 'revenueMax',
    INVEST_PROFILE_VIEW_TAB: INVEST_PROFILES_VIEW_TABS.FINANCIAL_METRICS,
    GA4: {
      [GA_EVENTS.portfolio_co_financials_complete]: GA_DIMENSIONS.revenue_range_min
    }
  },
  REVENUE_RANGE_MAX: {
    NAME: 'revenueMax',
    ID: 'profile_revenue_range_max',
    PLACEHOLDER: 'Max',
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    REQUIRED: true,
    INVEST_PROFILE_VIEW_TAB: INVEST_PROFILES_VIEW_TABS.FINANCIAL_METRICS,
    GA4: {
      [GA_EVENTS.portfolio_co_financials_complete]: GA_DIMENSIONS.revenue_range_max
    }
  },
  EBITDA_RANGE_MIN: {
    NAME: 'ebitdaMin',
    ID: 'pprofile_ebitda_range_min',
    LABEL: 'EBITDA range',
    LABEL_DESCRIPTION: 'Target EBITDA (€M)',
    PLACEHOLDER: 'Min',
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    REQUIRED: true,
    PAIR_NAME: 'ebitdaMax',
    INVEST_PROFILE_VIEW_TAB: INVEST_PROFILES_VIEW_TABS.FINANCIAL_METRICS,
    GA4: {
      [GA_EVENTS.portfolio_co_financials_complete]: GA_DIMENSIONS.EBITDA_range_min
    }
  },
  EBITDA_RANGE_MAX: {
    NAME: 'ebitdaMax',
    ID: 'profile_ebitda_range_max',
    PLACEHOLDER: 'Max',
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    REQUIRED: true,
    INVEST_PROFILE_VIEW_TAB: INVEST_PROFILES_VIEW_TABS.FINANCIAL_METRICS,
    GA4: {
      [GA_EVENTS.portfolio_co_financials_complete]: GA_DIMENSIONS.EBITDA_range_max
    }
  },
  EBITDA_MARGIN: {
    NAME: 'ebitdaMargin',
    ID: 'profile_ebitda_margin',
    LABEL: 'EBITDA margin (min.)',
    LABEL_DESCRIPTION: 'Target margin (%)',
    PLACEHOLDER: 'Value %',
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    REQUIRED: true,
    INVEST_PROFILE_VIEW_TAB: INVEST_PROFILES_VIEW_TABS.FINANCIAL_METRICS,
    GA4: {
      [GA_EVENTS.portfolio_co_financials_complete]: GA_DIMENSIONS.EBITDA_margin
    }
  },
  EBIT_RANGE_MIN: {
    NAME: 'ebitMin',
    ID: 'profile_ebit_range_min',
    LABEL: 'EBIT range',
    LABEL_DESCRIPTION: 'Target EBIT (€M)',
    PLACEHOLDER: 'Min',
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    REQUIRED: true,
    PAIR_NAME: 'ebitMax',
    INVEST_PROFILE_VIEW_TAB: INVEST_PROFILES_VIEW_TABS.FINANCIAL_METRICS,
    GA4: {
      [GA_EVENTS.portfolio_co_financials_complete]: GA_DIMENSIONS.EBIT_range_min
    }
  },
  EBIT_RANGE_MAX: {
    NAME: 'ebitMax',
    ID: 'profile_ebit_range_max',
    PLACEHOLDER: 'Max',
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    REQUIRED: true,
    INVEST_PROFILE_VIEW_TAB: INVEST_PROFILES_VIEW_TABS.FINANCIAL_METRICS,
    GA4: {
      [GA_EVENTS.portfolio_co_financials_complete]: GA_DIMENSIONS.EBIT_range_max
    }
  },
  EBIT_MARGIN: {
    NAME: 'ebitMargin',
    ID: 'profile_ebit_margin',
    LABEL: 'EBIT margin (min.)',
    LABEL_DESCRIPTION: 'Target margin (%)',
    PLACEHOLDER: 'Value %',
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    REQUIRED: true,
    INVEST_PROFILE_VIEW_TAB: INVEST_PROFILES_VIEW_TABS.FINANCIAL_METRICS,
    GA4: {
      [GA_EVENTS.portfolio_co_financials_complete]: GA_DIMENSIONS.EBIT_margin
    }
  },
  ENTERPRISE_RANGE_MIN: {
    NAME: 'enterpriseMin',
    ID: 'profile_enterprise_value_range_min',
    LABEL: 'Enterprise value range',
    LABEL_DESCRIPTION: 'Target value (€M)',
    PLACEHOLDER: 'Min',
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    REQUIRED: true,
    PAIR_NAME: 'enterpriseMax',
    INVEST_PROFILE_VIEW_TAB: INVEST_PROFILES_VIEW_TABS.VALUATION,
    GA4: {
      [GA_EVENTS.portfolio_co_valuation_complete]: GA_DIMENSIONS.enterprise_value_range_min
    }
  },
  ENTERPRISE_RANGE_MAX: {
    NAME: 'enterpriseMax',
    ID: 'profile_enterprise_value_range_max',
    PLACEHOLDER: 'Max',
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    REQUIRED: true,
    INVEST_PROFILE_VIEW_TAB: INVEST_PROFILES_VIEW_TABS.VALUATION,
    GA4: {
      [GA_EVENTS.portfolio_co_valuation_complete]: GA_DIMENSIONS.enterprise_value_range_max
    }
  },
  EQUITY_RANGE_MIN: {
    NAME: 'equityMin',
    ID: 'profile_equity_value_range_min',
    LABEL: 'Equity value range',
    LABEL_DESCRIPTION: 'Target value (€M)',
    PLACEHOLDER: 'Min',
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    REQUIRED: true,
    PAIR_NAME: 'equityMax',
    INVEST_PROFILE_VIEW_TAB: INVEST_PROFILES_VIEW_TABS.VALUATION,
    GA4: {
      [GA_EVENTS.portfolio_co_valuation_complete]: GA_DIMENSIONS.equity_value_range_min
    }
  },
  EQUITY_RANGE_MAX: {
    NAME: 'equityMax',
    ID: 'profile_equity_value_range_max',
    PLACEHOLDER: 'Max',
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    REQUIRED: true,
    INVEST_PROFILE_VIEW_TAB: INVEST_PROFILES_VIEW_TABS.VALUATION,
    GA4: {
      [GA_EVENTS.portfolio_co_valuation_complete]: GA_DIMENSIONS.equity_value_range_max
    }
  }
};

export const PROJECT_FIELDS = {
  NAME: {
    NAME: 'name',
    ID: 'project_name',
    PLACEHOLDER: 'Project name',
    LABEL: 'Project name',
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_TEXT,
    SHOW_MESSAGES_ON_INPUT_VALUE: true,
    VALIDATE: (value = '') => {
      return [generateMessage('Project name maximum text length is 40', value.length <= 40)];
    }
  },
  SEARCH_REASON: {
    NAME: 'searchReason',
    ID: 'project_search_reason',
    PLACEHOLDER: 'Select search reason',
    LABEL: 'Search reason',
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_SELECT,
    VALUES_KEY: OPTION_KEYS.SEARCH_REASONS
  }
};

export const PROFILER_PROJECT_FIELDS = {
  NAME: {
    NAME: 'name',
    ID: 'profiler_project_name',
    PLACEHOLDER: 'Project name',
    LABEL: 'Project name',
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_TEXT,
    SHOW_MESSAGES_ON_INPUT_VALUE: true,
    VALIDATE: (value = '') => {
      return [generateMessage('Project name maximum text length is 40', value.length <= 40)];
    }
  },
  SEARCH_REASON: {
    NAME: 'searchReason',
    ID: 'profiler_project_search_reason',
    PLACEHOLDER: 'Select search reason',
    LABEL: 'Search reason',
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_SELECT,
    VALUES_KEY: OPTION_KEYS.SEARCH_REASONS
  }
};

export const SAVED_SEARCH_VIEW_FIELDS = {
  RESULTS_COUNT: {
    NAME: 'resultsCount',
    ID: 'saved_search_res_count',
    LABEL: 'Number of results'
  },
  SAVED_DATE: {
    NAME: 'savedDate',
    ID: 'saved_search_saved_date',
    LABEL: 'Date saved'
  }
};

export const PROFILER_SEARCH_FIELDS = {
  SEARCH_TYPE: {
    NAME: 'searchType',
    ID: 'search_type_profiler',
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_RADIO,
    VALUES_KEY: UI_OPTION_KEYS.SEARCH_TYPE
  },
  INDUSTRY: {
    NAME: 'industry',
    ID: 'profiler_industry',
    LABEL: 'Industry',
    LABEL_INFO: 'The broader market segment\n(e.g., Automotive, IT Consulting)',
    PLACEHOLDER: 'Keyword 1, Keyword 2, ...',
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_TEXT,
    VALIDATE: (value = '') => {
      return [generateMessage('Industry maximum text length is 1000', value.length <= 1000)];
    }
  },
  VALUE_CHAIN: {
    NAME: 'valueChain',
    ID: 'profiler_value_chain',
    LABEL: 'Value chain',
    LABEL_INFO:
      'What the company does\n(e.g., Manufacturer, Service Provider,\nSoftware Development)',
    PLACEHOLDER: 'Keyword 1, Keyword 2, ...',
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_TEXT,
    VALIDATE: (value = '') => {
      return [generateMessage('Value chain maximum text length is 1000', value.length <= 1000)];
    }
  },
  PRODUCT_PORTFOLIO: {
    NAME: 'productPortfolio',
    ID: 'profiler_product_portfolio',
    LABEL: 'Product portfolio',
    LABEL_INFO: 'The specific output or solution\n(e.g., Automotive Parts, IoT Platforms)',
    PLACEHOLDER: 'Keyword 1, Keyword 2, ...',
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_TEXT,
    VALIDATE: (value = '') => {
      return [
        generateMessage('Product portfolio maximum text length is 1000', value.length <= 1000)
      ];
    }
  },
  REVENUE_MIN: {
    NAME: 'revenueMin',
    ID: 'profiler_revenue_min',
    LABEL: 'Sales (€M)',
    PLACEHOLDER: 'Min',
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    PAIR_NAME: 'revenueMax',
    ALLOW_SINGLE_RANGE: true
  },
  REVENUE_MAX: {
    NAME: 'revenueMax',
    ID: 'profiler_revenue_max',
    PLACEHOLDER: 'Max',
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    PAIR_NAME: 'revenueMin',
    ALLOW_SINGLE_RANGE: true
  },
  CAGR_MIN: {
    NAME: 'cagrMin',
    ID: 'profiler_cagr_min',
    LABEL: 'CAGR (%)',
    LABEL_INFO:
      'Calculated as compound annual\ngrowth rate based on revenue or\ngross profit. Period depends on\navailable data.',
    PLACEHOLDER: 'Min',
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    PAIR_NAME: 'cagrMax',
    ALLOW_SINGLE_RANGE: true,
    SECTION: isProfilerAdvancedCriteriaDisabled
      ? undefined
      : PROFILER_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
  },
  CAGR_MAX: {
    NAME: 'cagrMax',
    ID: 'profiler_cagr_max',
    PLACEHOLDER: 'Max',
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    SECTION: isProfilerAdvancedCriteriaDisabled
      ? undefined
      : PROFILER_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
  },
  CAGR_PERIOD: {
    NAME: 'cagrPeriod',
    ID: 'profiler_cagr_period',
    LABEL: 'CAGR Period',
    PLACEHOLDER: 'Select',
    TYPE: FIELDS_TYPES.TYPE_SELECT,
    VALUES_KEY: OPTION_KEYS.PROFILER_CAGR_PERIODS,
    SECTION: isProfilerAdvancedCriteriaDisabled
      ? undefined
      : PROFILER_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
  },
  EBIT_MARGIN_MIN: {
    NAME: 'ebitMarginMin',
    ID: 'profiler_ebit_margin_min',
    LABEL: 'EBIT Margin (%)',
    LABEL_INFO: 'Most recent year',
    PLACEHOLDER: 'Min',
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    PAIR_NAME: 'ebitMarginMax',
    ALLOW_SINGLE_RANGE: true,
    SECTION: isProfilerAdvancedCriteriaDisabled
      ? undefined
      : PROFILER_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
  },
  EBIT_MARGIN_MAX: {
    NAME: 'ebitMarginMax',
    ID: 'profiler_ebit_margin_max',
    PLACEHOLDER: 'Max',
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    SECTION: isProfilerAdvancedCriteriaDisabled
      ? undefined
      : PROFILER_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
  },
  EMPLOYEES_MIN: {
    NAME: 'employeesMin',
    ID: 'profiler_employees_min',
    LABEL: 'Employees',
    PLACEHOLDER: 'Min',
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    INTEGER: true,
    PAIR_NAME: 'employeesMax',
    SECTION: PROFILER_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA,
    MIN: 0,
    ALLOW_SINGLE_RANGE: true
  },
  EMPLOYEES_MAX: {
    NAME: 'employeesMax',
    ID: 'profiler_employees_max',
    PLACEHOLDER: 'Max',
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    INTEGER: true,
    SECTION: PROFILER_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA,
    MIN: 0
  },
  HEADQUARTER: {
    NAME: 'headquarter',
    ID: 'profiler_headquarter',
    PLACEHOLDER: 'Select',
    LABEL: 'Country',
    TYPE: FIELDS_TYPES.TYPE_MULTI_SELECT,
    VALUES_KEY: OPTION_KEYS.PROFILER_HEADQUARTER_COUNTRIES,
    SECTION: PROFILER_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
  },
  SITUATION: {
    NAME: 'situation',
    ID: 'profiler_situation',
    LABEL: 'Situation',
    PLACEHOLDER: 'Select',
    TYPE: FIELDS_TYPES.TYPE_MULTI_SELECT,
    VALUES_KEY: OPTION_KEYS.PROFILER_SITUATIONS,
    SECTION:
      isProfilerAdvancedCriteriaDisabled || isProfilerAdvancedCriteriaSituationDisabled
        ? undefined
        : PROFILER_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
  },
  // Ownership criteria: three standard dropdowns. The chain position works like CAGR
  // period: disabled until a structure is chosen, then required.
  LISTING_STATUS: {
    NAME: 'listingStatus',
    ID: 'profiler_listing_status',
    LABEL: 'Ownership',
    PLACEHOLDER: 'Select',
    TYPE: FIELDS_TYPES.TYPE_MULTI_SELECT,
    VALUES: LISTING_STATUSES,
    VALUES_KEY: UI_OPTION_KEYS.PROFILER_LISTING_STATUS,
    SECTION: isOwnershipCriteriaHidden
      ? undefined
      : PROFILER_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
  },
  OWNERSHIP_STRUCTURES: {
    NAME: 'ownershipStructures',
    ID: 'profiler_ownership_structures',
    LABEL: 'Ownership structure',
    PLACEHOLDER: 'Select',
    TYPE: FIELDS_TYPES.TYPE_MULTI_SELECT,
    VALUES: OWNERSHIP_STRUCTURES,
    VALUES_KEY: UI_OPTION_KEYS.PROFILER_OWNERSHIP_STRUCTURE,
    SECTION: isOwnershipCriteriaHidden
      ? undefined
      : PROFILER_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
  },
  OWNERSHIP_BASIS: {
    NAME: 'ownershipBasis',
    ID: 'profiler_ownership_basis',
    LABEL: 'Ownership chain position',
    PLACEHOLDER: 'Select',
    TYPE: FIELDS_TYPES.TYPE_SELECT,
    VALUES: OWNERSHIP_BASES,
    VALUES_KEY: UI_OPTION_KEYS.PROFILER_OWNERSHIP_BASIS,
    SECTION: isOwnershipCriteriaHidden
      ? undefined
      : PROFILER_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
  },
  // Geo location fields
  CITY_ZIPCODE: {
    NAME: 'cityZipcode',
    ID: 'profiler_city_zipcode',
    LABEL: 'City / Zipcode',
    PLACEHOLDER: 'e.g., Frankfurt am Main or 60313',
    TYPE: FIELDS_TYPES.TYPE_TEXT,
    DISABLE_AUTOFILL: true,
    INPUT_AUTOCOMPLETE: 'profiler_city_zipcode',
    SECTION: PROFILER_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA,
    SHOW_MESSAGES_ON_INPUT_VALUE: true,
    VALIDATE: (value = '') => {
      if (value.length === 0) {
        return []; // Optional field
      }

      // Check if it's a 5-digit German postal code
      const isPostalCode = /^\d{5}$/.test(value);
      if (isPostalCode) {
        return []; // Valid postal code
      }

      // Validate as city name (allow letters, spaces, hyphen, apostrophe, umlauts, and optional region in parentheses)
      const messages = [];
      messages.push(generateMessage('City name must be at least 2 characters', value.length >= 2));
      messages.push(generateMessage('City name maximum text length is 50', value.length <= 50));
      // The spaces before the region belong to the name: a \s* there backtracked.
      const cityPattern = /^[a-zA-ZäöüßÄÖÜ\s\-']+(?:\([^)]*\))?$/;
      messages.push(
        generateMessage(
          'Enter either a city name or a 5-digit postal code',
          cityPattern.test(value)
        )
      );

      return messages;
    }
  },
  DISTANCE: {
    NAME: 'distance',
    ID: 'profiler_distance',
    LABEL: 'Distance (km)',
    PLACEHOLDER: 'Distance',
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    INTEGER: true,
    MIN: 1,
    MAX: 200,
    DISABLE_AUTOFILL: true,
    INPUT_AUTOCOMPLETE: 'profiler_distance',
    SECTION: PROFILER_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA,
    SHOW_MESSAGES_ON_INPUT_VALUE: true,
    VALIDATE: (value = '') => {
      if (value.length === 0) {
        return []; // Optional field
      }

      const [min, max] = [1, 200];
      const num = +value;
      let isValid = true;

      if (isNaN(num) || num < min || num > max || !Number.isInteger(num)) {
        isValid = false;
      }

      return [generateMessage(`Must be ${min}-${max} km`, isValid)];
    }
  }
};

export const PROFILER_INDUSTRY_CODES_SEARCH_FIELDS = {
  SEARCH_TYPE: {
    NAME: 'searchType',
    ID: 'ic_search_type_profiler',
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_RADIO,
    VALUES_KEY: UI_OPTION_KEYS.SEARCH_TYPE
  },
  INDUSTRY_CODES: {
    NAME: 'industryCodes',
    ID: 'ic_profiler_industry',
    LABEL: 'Industries',
    PLACEHOLDER: 'Select',
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_MULTI_SELECT
  },
  REVENUE_MIN: {
    NAME: 'revenueMin',
    ID: 'ic_profiler_revenue_min',
    LABEL: 'Sales (€M)',
    PLACEHOLDER: 'Min',
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    PAIR_NAME: 'revenueMax',
    ALLOW_SINGLE_RANGE: true
  },
  REVENUE_MAX: {
    NAME: 'revenueMax',
    ID: 'ic_profiler_revenue_max',
    PLACEHOLDER: 'Max',
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    PAIR_NAME: 'revenueMin',
    ALLOW_SINGLE_RANGE: true
  },
  CAGR_MIN: {
    NAME: 'cagrMin',
    ID: 'ic_profiler_cagr_min',
    LABEL: 'CAGR (%)',
    LABEL_INFO:
      'Calculated as compound annual\ngrowth rate based on revenue or\ngross profit. Period depends on\navailable data.',
    PLACEHOLDER: 'Min',
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    PAIR_NAME: 'cagrMax',
    ALLOW_SINGLE_RANGE: true,
    SECTION: isProfilerAdvancedCriteriaDisabled
      ? undefined
      : PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
  },
  CAGR_MAX: {
    NAME: 'cagrMax',
    ID: 'ic_profiler_cagr_max',
    PLACEHOLDER: 'Max',
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    SECTION: isProfilerAdvancedCriteriaDisabled
      ? undefined
      : PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
  },
  CAGR_PERIOD: {
    NAME: 'cagrPeriod',
    ID: 'ic_profiler_cagr_period',
    LABEL: 'CAGR Period',
    PLACEHOLDER: 'Select',
    TYPE: FIELDS_TYPES.TYPE_SELECT,
    VALUES_KEY: OPTION_KEYS.PROFILER_CAGR_PERIODS,
    SECTION: isProfilerAdvancedCriteriaDisabled
      ? undefined
      : PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
  },
  EBIT_MARGIN_MIN: {
    NAME: 'ebitMarginMin',
    ID: 'ic_profiler_ebit_margin_min',
    LABEL: 'EBIT Margin (%)',
    LABEL_INFO: 'Most recent year',
    PLACEHOLDER: 'Min',
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    PAIR_NAME: 'ebitMarginMax',
    ALLOW_SINGLE_RANGE: true,
    SECTION: isProfilerAdvancedCriteriaDisabled
      ? undefined
      : PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
  },
  EBIT_MARGIN_MAX: {
    NAME: 'ebitMarginMax',
    ID: 'ic_profiler_ebit_margin_max',
    PLACEHOLDER: 'Max',
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    SECTION: isProfilerAdvancedCriteriaDisabled
      ? undefined
      : PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
  },
  EMPLOYEES_MIN: {
    NAME: 'employeesMin',
    ID: 'ic_profiler_employees_min',
    LABEL: 'Employees',
    PLACEHOLDER: 'Min',
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    INTEGER: true,
    PAIR_NAME: 'employeesMax',
    SECTION: PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA,
    MIN: 0,
    ALLOW_SINGLE_RANGE: true
  },
  EMPLOYEES_MAX: {
    NAME: 'employeesMax',
    ID: 'ic_profiler_employees_max',
    PLACEHOLDER: 'Max',
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    INTEGER: true,
    SECTION: PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA,
    MIN: 0
  },
  HEADQUARTER: {
    NAME: 'headquarter',
    ID: 'ic_profiler_headquarter',
    PLACEHOLDER: 'Select',
    LABEL: 'Country',
    TYPE: FIELDS_TYPES.TYPE_MULTI_SELECT,
    VALUES_KEY: OPTION_KEYS.PROFILER_HEADQUARTER_COUNTRIES,
    SECTION: PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
  },
  // Geo location fields (shared with characteristics search)
  CITY_ZIPCODE: {
    ...PROFILER_SEARCH_FIELDS.CITY_ZIPCODE,
    ID: 'ic_profiler_city_zipcode',
    SECTION: PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
  },
  DISTANCE: {
    ...PROFILER_SEARCH_FIELDS.DISTANCE,
    ID: 'ic_profiler_distance',
    SECTION: PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
  },
  SITUATION: {
    NAME: 'situation',
    ID: 'ic_profiler_situation',
    LABEL: 'Situation',
    PLACEHOLDER: 'Select',
    TYPE: FIELDS_TYPES.TYPE_MULTI_SELECT,
    VALUES_KEY: OPTION_KEYS.PROFILER_SITUATIONS,
    SECTION:
      isProfilerAdvancedCriteriaDisabled || isProfilerAdvancedCriteriaSituationDisabled
        ? undefined
        : PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
  },
  // Same NAMEs as Characteristics; saved-project views read both with those constants.
  LISTING_STATUS: {
    ...PROFILER_SEARCH_FIELDS.LISTING_STATUS,
    ID: 'ic_profiler_listing_status',
    SECTION: isOwnershipCriteriaHidden
      ? undefined
      : PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
  },
  OWNERSHIP_STRUCTURES: {
    ...PROFILER_SEARCH_FIELDS.OWNERSHIP_STRUCTURES,
    ID: 'ic_profiler_ownership_structures',
    SECTION: isOwnershipCriteriaHidden
      ? undefined
      : PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
  },
  OWNERSHIP_BASIS: {
    ...PROFILER_SEARCH_FIELDS.OWNERSHIP_BASIS,
    ID: 'ic_profiler_ownership_basis',
    SECTION: isOwnershipCriteriaHidden
      ? undefined
      : PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
  }
};

export const COMPANY_DETAILS_SHAREHOLDER_POPUP_FIELDS = {
  SHAREHOLDER_SHARE_PERCENT: {
    NAME: 'shareholderSharePercent',
    ID: 'shareholder_share_percent',
    LABEL: 'With a minimum percentage of ownership',
    PLACEHOLDER: 'e.g. 5',
    REQUIRED: true,
    TYPE: FIELDS_TYPES.TYPE_NUMBER,
    MIN: 0,
    INTEGER: true
  },
  ENTITIES_UNKNOWN_VALUE: {
    NAME: 'entitiesUnknownValue',
    ID: 'entities_unknown_value',
    LABEL: 'Include entities with an unknown value',
    TYPE: FIELDS_TYPES.TYPE_CHECKBOX
  },
  INCLUDE_SUBSIDIARIES: {
    NAME: 'includeSubsidiaries',
    ID: 'include_subsidiaries',
    LABEL: 'Include subsidiaries',
    TYPE: FIELDS_TYPES.TYPE_CHECKBOX
  },
  EXPAND_GENERAL_PARTNERS: {
    NAME: 'expandGeneralPartners',
    ID: 'expand_general_partners',
    LABEL: 'Show owners of general partners',
    TYPE: FIELDS_TYPES.TYPE_CHECKBOX
  }
};

export const EMPLOYEES_FILTERS_FIELDS = {
  ROLE: {
    NAME: 'role',
    ID: 'ef_role',
    TYPE: FIELDS_TYPES.TYPE_MULTI_SELECT,
    PLACEHOLDER: 'Role',
    LABEL: 'By role'
  },
  COUNTRY: {
    NAME: 'country',
    ID: 'ef_country',
    TYPE: FIELDS_TYPES.TYPE_MULTI_SELECT,
    PLACEHOLDER: 'Country of residence',
    LABEL: 'By country of residence'
  },
  IS_SHAREHOLDER: {
    NAME: 'isShareholder',
    ID: 'ef_shareholder',
    TYPE: FIELDS_TYPES.TYPE_MULTI_SELECT,
    PLACEHOLDER: 'Is Shareholder',
    LABEL: 'Is Shareholder',
    VALUES_KEY: UI_OPTION_KEYS.CONTACT_FILTER_IS_SHAREHOLDER
  },
  AGE: {
    NAME: 'age',
    ID: 'ef_age',
    TYPE: FIELDS_TYPES.TYPE_MULTI_SELECT,
    PLACEHOLDER: 'Age',
    LABEL: 'Age',
    VALUES_KEY: UI_OPTION_KEYS.CONTACT_FILTER_AGE
  }
};

export const getErrorFields = (descrFields = [], stateFields = {}, onSubmitFields = []) => {
  const validationObj = {
    messages: [],
    invalidFields: []
  };

  for (const field of descrFields) {
    const isOnSubmit = onSubmitFields.includes(field);
    if (
      typeof field.VALIDATE === 'function' &&
      (field.TYPE === FIELDS_TYPES.TYPE_CHECKBOX ||
        typeof stateFields[field.NAME] === 'number' ||
        stateFields[field.NAME].length)
    ) {
      const messages = field.VALIDATE(stateFields[field.NAME]);
      if (messages.findIndex(({ isValid }) => !isValid) !== -1) {
        validationObj.invalidFields.push({ name: field.NAME, isOnSubmit });
        if (
          !(
            field.SHOW_MESSAGES_ON_INPUT_VALUE &&
            typeof stateFields[field.NAME] === 'string' &&
            !stateFields[field.NAME].length
          )
        ) {
          validationObj.messages.push(
            ...messages.map(({ isValid, msg }) =>
              generateMessage(msg, isValid, field.NAME, isOnSubmit)
            )
          );
        }
      }
    } else if (
      field.REQUIRED &&
      ((field.TYPE === FIELDS_TYPES.TYPE_NUMBER && isNaN(stateFields[field.NAME])) ||
        !stateFields[field.NAME] ||
        ([
          FIELDS_TYPES.TYPE_MULTI_SELECT,
          FIELDS_TYPES.TYPE_SELECT,
          FIELDS_TYPES.TYPE_RADIO
        ].includes(field.TYPE) &&
          !stateFields[field.NAME].length))
    ) {
      if (field.PAIR_NAME) {
        const pairRangeVal = stateFields[field.PAIR_NAME];
        if (pairRangeVal == null || pairRangeVal == '') {
          validationObj.invalidFields.push({ name: field.NAME, isOnSubmit });
        }
      } else {
        validationObj.invalidFields.push({ name: field.NAME, isOnSubmit });
      }
      if (typeof field.VALIDATE === 'function') {
        const messages = field.VALIDATE(stateFields[field.NAME]);
        if (
          !(
            field.SHOW_MESSAGES_ON_INPUT_VALUE &&
            typeof stateFields[field.NAME] === 'string' &&
            !stateFields[field.NAME].length
          )
        ) {
          validationObj.messages.push(
            ...messages.map(({ isValid, msg }) =>
              generateMessage(msg, isValid, field.NAME, isOnSubmit)
            )
          );
        }
      }
    }

    if (field.PAIR_NAME) {
      const pairField = descrFields.find((f) => f.NAME === field.PAIR_NAME);
      const rangeMin = field.NAME.endsWith('Min')
        ? stateFields[field.NAME]
        : stateFields[field.PAIR_NAME];
      const rangeMax = field.NAME.endsWith('Max')
        ? stateFields[field.NAME]
        : stateFields[field.PAIR_NAME];
      if (rangeMin.length && rangeMax.length) {
        const [rangeMinNum, rangeMaxNum] = [+rangeMin, +rangeMax];
        if (!isNaN(rangeMinNum) && !isNaN(rangeMaxNum)) {
          if (rangeMinNum > rangeMaxNum) {
            validationObj.invalidFields.push(
              { name: field.NAME, isOnSubmit: true },
              { name: field.PAIR_NAME, isOnSubmit: true }
            );
            validationObj.messages.push(
              generateMessage('min < max values required', false, field.NAME, true),
              generateMessage('', false, field.PAIR_NAME, true)
            );
          } else if (typeof field.MIN === 'number' && rangeMinNum < field.MIN) {
            validationObj.invalidFields.push(
              { name: field.NAME, isOnSubmit: true },
              { name: field.PAIR_NAME, isOnSubmit: true }
            );
            validationObj.messages.push(
              generateMessage(`min value >= ${field.MIN} required`, false, field.NAME, true),
              generateMessage('', false, field.PAIR_NAME, true)
            );
          } else if (typeof field.MAX === 'number' && rangeMinNum < field.MAX) {
            validationObj.invalidFields.push(
              { name: field.NAME, isOnSubmit: true },
              { name: field.PAIR_NAME, isOnSubmit: true }
            );
            validationObj.messages.push(
              generateMessage(`min value <= ${field.MAX} required`, false, field.NAME, true),
              generateMessage('', false, field.PAIR_NAME, true)
            );
          } else if (typeof pairField.MIN === 'number' && rangeMaxNum < pairField.MIN) {
            validationObj.invalidFields.push(
              { name: field.NAME, isOnSubmit: true },
              { name: field.PAIR_NAME, isOnSubmit: true }
            );
            validationObj.messages.push(
              generateMessage('', false, field.NAME, true),
              generateMessage(
                `max value >= ${pairField.MIN} required`,
                false,
                field.PAIR_NAME,
                true
              )
            );
          } else if (typeof pairField.MAX === 'number' && rangeMaxNum < pairField.MAX) {
            validationObj.invalidFields.push(
              { name: field.NAME, isOnSubmit: true },
              { name: field.PAIR_NAME, isOnSubmit: true }
            );
            validationObj.messages.push(
              generateMessage('', false, field.NAME, true),
              generateMessage(
                `max value <= ${pairField.MAX} required`,
                false,
                field.PAIR_NAME,
                true
              )
            );
          }
        } else if (isNaN(rangeMinNum)) {
          validationObj.invalidFields.push(
            { name: field.NAME, isOnSubmit: true },
            { name: field.PAIR_NAME, isOnSubmit: true }
          );
          validationObj.messages.push(
            generateMessage('min value must be a number', false, field.NAME, true),
            generateMessage('', false, field.PAIR_NAME, true)
          );
        } else if (isNaN(rangeMaxNum)) {
          validationObj.invalidFields.push(
            { name: field.NAME, isOnSubmit: true },
            { name: field.PAIR_NAME, isOnSubmit: true }
          );
          validationObj.messages.push(
            generateMessage('', false, field.NAME, true),
            generateMessage('max value must be a number', false, field.PAIR_NAME, true)
          );
        }
      } else if (rangeMin.length || rangeMax.length) {
        if (!field.ALLOW_SINGLE_RANGE) {
          validationObj.invalidFields.push(
            { name: field.NAME, isOnSubmit: true },
            { name: field.PAIR_NAME, isOnSubmit: true }
          );
          validationObj.messages.push(
            generateMessage(
              `${rangeMin.length ? 'max' : 'min'} value missing`,
              false,
              field.NAME,
              true
            ),
            generateMessage('', false, field.PAIR_NAME, true)
          );
        }
      }
    }
  }

  return validationObj;
};

export const compareFields = (stateFields = {}, objectFields = {}) => {
  return !!Object.entries(stateFields).find(
    ([fieldName, fieldValue]) =>
      (typeof objectFields[fieldName] === 'string'
        ? objectFields[fieldName]
        : objectFields[fieldName] && typeof objectFields[fieldName] !== 'boolean'
          ? JSON.stringify(objectFields[fieldName])
          : '') !== (typeof fieldValue === 'string' ? fieldValue : JSON.stringify(fieldValue))
  );
};

export const trimStateFields = (stateFields = {}) => {
  runInAction(() => {
    Object.entries(stateFields).forEach(([fieldName, fieldValue]) => {
      if (typeof fieldValue === 'string') {
        stateFields[fieldName] = fieldValue.trim();
      }
    });
  });
};

export const isEmptyValue = (value) => {
  if (typeof value === 'boolean' && !value) {
    return true;
  }

  if ((typeof value === 'string' || Array.isArray(value)) && !value.length) {
    return true;
  }

  if (value === undefined || value === null) {
    return true;
  }

  return false;
};
